import React, { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";

/**
 * ShareCard — share buttons (WhatsApp, Telegram, Email, Copy link)
 * + downloadable high-resolution QR code (1024x1024 PNG with margin & logo-friendly).
 */
export default function ShareCard({ url, title = "" }) {
  const [copied, setCopied] = useState(false);
  const hiddenQrRef = useRef(null);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const shareText = title ? `${title} — su HostMarket` : "Guarda questo annuncio su HostMarket";

  const enc = encodeURIComponent;
  const wa = `https://wa.me/?text=${enc(`${shareText}\n${shareUrl}`)}`;
  const tg = `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(shareText)}`;
  const mail = `mailto:?subject=${enc(shareText)}&body=${enc(`${shareText}\n\n${shareUrl}`)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadQrHD = () => {
    // QRCodeCanvas renders at the requested `size`. We render an off-screen 1024px canvas
    // and trigger a PNG download.
    const canvas = hiddenQrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png", 1.0);
    const a = document.createElement("a");
    const safe = (title || "hostmarket").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "hostmarket";
    a.href = dataUrl;
    a.download = `qr-${safe}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const Btn = ({ onClick, href, color, icon, label, testid }) => {
    const cls = `flex items-center justify-center gap-2 h-11 rounded-lg border border-zinc-800 bg-[#0A0A0A] hover:bg-[#111] hover:border-${color} transition-all text-xs font-bold uppercase tracking-wider text-zinc-200 hover:text-white`;
    return href ? (
      <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>
        <i className={icon}></i>
        <span>{label}</span>
      </a>
    ) : (
      <button type="button" onClick={onClick} className={cls} data-testid={testid}>
        <i className={icon}></i>
        <span>{label}</span>
      </button>
    );
  };

  return (
    <div className="hm-card p-5" data-testid="share-card">
      <div className="text-xs uppercase tracking-widest text-[#FFBE00] mb-3 flex items-center gap-2">
        <i className="fas fa-share-nodes"></i><span>Condividi annuncio</span>
      </div>

      {/* Share buttons grid */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <Btn href={wa} color="[#25D366]" icon="fab fa-whatsapp text-[#25D366]" label="WhatsApp" testid="share-whatsapp" />
        <Btn href={tg} color="[#229ED9]" icon="fab fa-telegram text-[#229ED9]" label="Telegram" testid="share-telegram" />
        <Btn href={mail} color="[#FFBE00]" icon="fas fa-envelope text-[#FFBE00]" label="Email" testid="share-email" />
        <Btn onClick={copyLink} color="[#E619A8]" icon={`fas ${copied ? "fa-check text-[#22C55E]" : "fa-link text-[#E619A8]"}`} label={copied ? "Copiato!" : "Copia link"} testid="share-copy-link" />
      </div>

      {/* QR Code preview */}
      <div className="rounded-lg bg-white p-3 flex items-center justify-center mb-3" data-testid="share-qr-preview">
        <QRCodeCanvas value={shareUrl} size={160} level="H" includeMargin={false} bgColor="#FFFFFF" fgColor="#000000" />
      </div>

      <button
        type="button"
        onClick={downloadQrHD}
        className="w-full h-11 rounded-lg bg-gradient-to-r from-[#FFBE00] to-[#E619A8] text-black font-black uppercase tracking-wider text-xs hover:brightness-110 transition-all flex items-center justify-center gap-2"
        data-testid="share-qr-download"
      >
        <i className="fas fa-qrcode"></i>
        <span>Scarica QR HD</span>
      </button>

      <p className="text-[11px] text-zinc-500 mt-2.5 leading-snug">
        Stampalo su biglietti da visita, capelli sull'ologramma del salone o ovunque vuoi.
        Inquadrandolo si aprirà l'annuncio.
      </p>

      {/* Hidden 1024px canvas for HD download */}
      <div ref={hiddenQrRef} style={{ position: "absolute", left: -99999, top: -99999, pointerEvents: "none" }} aria-hidden="true">
        <QRCodeCanvas value={shareUrl} size={1024} level="H" includeMargin={true} bgColor="#FFFFFF" fgColor="#000000" />
      </div>
    </div>
  );
}
